import React from 'react';
import { useAudioMixer } from '../context/AudioMixerContext';
import { useMediaPlayer } from '@vidstack/react';
import { Flex, Box, Popover, Slider, Text, Separator } from '@radix-ui/themes';
import { useTranslation } from 'react-i18next';

import styles from '../styles/SmartPlayer.module.css';
import { PlayerIconButton, SolidPopoverContent } from './SmartPlayerUI';

export const SmartPlayerChannelControllerPanel: React.FC = () => {
    const { t } = useTranslation('smartPlayer');
    const { channels, setVolume, toggleMute, toggleSolo } = useAudioMixer();
    const player = useMediaPlayer();

    if (channels.length === 0) return null;

    return (
        <Popover.Root>
            <Popover.Trigger>
                <PlayerIconButton
                    style={{ width: 'auto', padding: '0 var(--space-2)' }}
                >
                    <Text size='2' weight='medium'>
                        {t('audio.mixer_button')} ({channels.length}{' '}
                        {t('audio.channels_short')})
                    </Text>
                </PlayerIconButton>
            </Popover.Trigger>

            <SolidPopoverContent
                container={player?.el || undefined}
                style={{ width: '280px', padding: 'var(--space-3)' }}
            >
                <Box mb='2'>
                    <Text
                        size='1'
                        weight='bold'
                        color='gray'
                        className={styles.menuHeader}
                    >
                        {t('audio.mixer_title')}
                    </Text>
                </Box>
                <Separator size='4' mb='3' style={{ opacity: 0.15 }} />

                <Flex direction='column' gap='3'>
                    {channels.map((ch) => (
                        <Flex key={ch.index} align='center' gap='3'>
                            <Box style={{ width: '40px' }}>
                                <Text
                                    size='1'
                                    weight='bold'
                                    style={{ color: 'var(--gray-11)' }}
                                >
                                    {t('audio.channel_short')} {ch.index + 1}
                                </Text>
                            </Box>

                            <Flex gap='1' align='center'>
                                <PlayerIconButton
                                    size='1'
                                    color={ch.isSoloed ? 'amber' : 'gray'}
                                    variant={ch.isSoloed ? 'solid' : 'outline'}
                                    onClick={() => toggleSolo(ch.index)}
                                    style={{
                                        borderColor: ch.isSoloed
                                            ? 'transparent'
                                            : 'var(--gray-a4)',
                                    }}
                                >
                                    {t('audio.solo_short')}
                                </PlayerIconButton>

                                <PlayerIconButton
                                    size='1'
                                    color={ch.isMuted ? 'red' : 'gray'}
                                    variant={ch.isMuted ? 'solid' : 'outline'}
                                    onClick={() => toggleMute(ch.index)}
                                    style={{
                                        borderColor: ch.isMuted
                                            ? 'transparent'
                                            : 'var(--gray-a4)',
                                    }}
                                >
                                    {t('audio.mute_short')}
                                </PlayerIconButton>
                            </Flex>

                            <Box flexGrow='1'>
                                <Slider
                                    size='1'
                                    min={0}
                                    max={1}
                                    step={0.01}
                                    value={[ch.isMuted ? 0 : ch.volume]}
                                    onValueChange={(val) =>
                                        typeof val[0] === 'number' &&
                                        setVolume(ch.index, val[0])
                                    }
                                    disabled={ch.isMuted}
                                    style={{
                                        opacity: ch.isMuted ? 0.25 : 1,
                                        transition: 'opacity 0.15s ease',
                                    }}
                                />
                            </Box>
                        </Flex>
                    ))}
                </Flex>
            </SolidPopoverContent>
        </Popover.Root>
    );
};
